import { z } from 'zod';
import { IntegratedApp } from '@/constants/apps';
import { postRequest, getRequest } from './amplify/helpers';
import { ProductMedia } from '@/types/product-service';

export const getProductsByTerm = async ({ appKey, appUrl, term = '', nextPageFrom }) => {
  try {
    const body = {
      appKey,
      appUrl,
      term,
      nextPageFrom,
    };

    return await postRequest({
      apiName: 'productActions',
      path: '/actions/v2/products/search',
      options: { body },
    });
  } catch (error) {
    console.error('Error in getProductsByTerm:', error);
  }
};

export const getElasticProductsByIds = async ({ appKey, appUrl, ids, term = '' }) => {
  try {
    const body = {
      appKey,
      appUrl,
      ids,
      isSearchByIds: true,
      term,
    };

    return await postRequest({
      apiName: 'productActions',
      path: '/actions/v2/products/search',
      options: { body },
    });
  } catch (error) {
    console.error('Error in getElasticProductsByIds:', error);
  }
};

export const getProductsByProductIds = async ({ externalProductIds, appKey }) => {
  try {
    const body = {
      productIds: externalProductIds,
      appKey,
    };

    return await postRequest({
      apiName: 'productActions',
      path: '/actions/v2/shopify/get',
      options: { body },
    });
  } catch (error) {
    console.error('Error in getProductsByProductIds:', error);
  }
};

export const getEcomProducts = async ({ externalProductIds, appKey }) => {
  try {
    const body = {
      productIds: externalProductIds,
      appKey,
    };

    return await postRequest({
      apiName: 'productActions',
      path: '/actions/v2/shopify/get-ecom-products',
      options: { body },
    });
  } catch (error) {
    console.error('Error in getEcomProducts:', error);
  }
};

export const getTotalProductsCount = async (appUrl: string) => {
  try {
    return await getRequest({
      apiName: 'productsService',
      path: '/total-count',
      options: {
        queryParams: {
          appUrl,
          appName: IntegratedApp.STUDIO_SHOPIFY,
        },
      },
    });
  } catch (error) {
    console.error('Error in getTotalProductsCount:', error);
    return 0;
  }
};

const GetProductMediaRequestSchema = z.object({
  productId: z.string(),
  appUrl: z.string(),
});
type GetProductMediaRequest = z.infer<typeof GetProductMediaRequestSchema>;
export const getProduct = async (query: GetProductMediaRequest): Promise<ProductMedia[]> => {
  try {
    const response = await getRequest({
      apiName: 'productsService',
      path: `/public/product/media`,
      options: {
        queryParams: {
          ...query,
          appName: IntegratedApp.STUDIO_SHOPIFY,
        },
      },
    });

    return response;
  } catch (error) {
    console.error('Error in getProductMedia:', error);
  }

  return [];
};

// Define the response type to match the actual server response
export interface ProductSearchResponse {
  items: Array<{
    _id: string;
    productType: string;
    title: string;
    tags: string;
    images?: Array<{
      url: string;
      [key: string]: any;
    }>;
    [key: string]: any;
  }>;
  total: number;
  from: number;
  itemsCount: number;
  nextPage: number;
}

const GetProductsQuerySchema = z.object({
  appUrl: z.string(),
  searchTerm: z.string().optional(),
  size: z.number().optional(),
  from: z.number().optional(),
  filter: z
    .object({
      productType: z.array(z.string()).optional(),
      category: z.array(z.string()).optional(),
      tags: z.array(z.string()).optional(),
      productName: z.array(z.string()).optional(),
      productId: z.array(z.string()).optional(),
      sku: z.array(z.string()).optional(),
      createdAt: z
        .object({
          from: z.string().optional(),
          to: z.string().optional(),
        })
        .optional(),
    })
    .optional(),
  sort: z
    .object({
      field: z.enum(['name', 'date']),
      order: z.enum(['asc', 'desc']),
    })
    .optional(),
});
type GetProductsQuery = z.infer<typeof GetProductsQuerySchema>;

export const getProducts = async (query: GetProductsQuery): Promise<ProductSearchResponse> => {
  try {
    // Clone the query to avoid modifying the original
    const requestBody = { ...query };

    // If searchTerm is empty or undefined, don't include it in the request
    // This will return all products instead of an empty search
    if (!requestBody.searchTerm) {
      delete requestBody.searchTerm;
    }

    const response = await postRequest({
      apiName: 'productsService',
      path: '/public/search-products',
      options: {
        body: {
          ...requestBody,
          appName: IntegratedApp.STUDIO_SHOPIFY,
        },
      },
    });

    return response;
  } catch (error) {
    console.error('Error in getProducts:', error);
    return { items: [], total: 0, from: 0, itemsCount: 0, nextPage: 0 };
  }
};

const GetProductFiltersSchema = z.object({
  appUrl: z.string(),
});
type GetProductFiltersQuery = z.infer<typeof GetProductFiltersSchema>;

export const getProductFilters = async (query: GetProductFiltersQuery) => {
  try {
    const response = await postRequest({
      apiName: 'productsService',
      path: '/public/product-filters',
      options: {
        body: {
          ...query,
          appName: IntegratedApp.STUDIO_SHOPIFY,
        },
      },
    });

    return response;
  } catch (error) {
    console.error('Error in getProductFilters:', error);
    return {
      productTypes: [],
      categories: [],
      tags: [],
    };
  }
};
